import React, { useState, useEffect } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import { FileWithType } from './MultiFileUpload';
import {
  knowledgeBaseDocumentApi,
  type KnowledgeBaseDocument,
} from '@/api/knowledge-base';
import {
  RiArrowLeftLine as BackIcon,
  RiSparkling2Line as MagicIcon,
  RiUploadLine as UploadIcon,
  RiFileTextLine as TextIcon,
  RiFileLine as FileIcon,
  RiDatabase2Line as DatabaseIcon,
  RiSearchLine as SearchIcon,
} from 'react-icons/ri';

export interface LetterCreatePayload {
  letter_type: string; // always 'recommendation'
  referee_name?: string;
  recommender_background?: string;
  recommender_document_id?: string;
  relationship_to_beneficiary?: string;
  achievements_to_highlight?: string;
  selected_criteria?: string[];
  selected_sections?: string[];
  whitelist?: string;
  blacklist?: string;
  files?: FileWithType[]; // Multiple files with types
  target_word_count?: number; // Target word count for letter length
  recommender_country?: string; // Country of recommender
  recommender_is_us_based?: boolean; // Whether recommender is US-based
  metadata?: {
    template_kb_document_id?: string;
    template_kb_document_title?: string;
    [key: string]: any;
  };
}

interface LetterCreateFormProps {
  onCancel: () => void;
  onCreate: (payload: LetterCreatePayload) => void | Promise<void>;
  isSubmitting?: boolean;
  caseId?: string;
  visaType?: string;
  error?: string | null;
  onClearError?: () => void;
}

// Visa-specific criteria mapping
const CRITERIA_BY_VISA_TYPE: Record<string, string[]> = {
  'EB-1A': [
    'Awards or Recognition',
    'Professional Memberships',
    'Published Material About You',
    'Judging Others',
    'Original Contributions',
    'Scholarly Articles',
    'Exhibitions or Showcases',
    'Leading or Critical Role',
    'High Salary or Remuneration',
    'Commercial Success',
  ],
  'O-1A': [
    'Awards and Recognition',
    'Membership in Distinguished Associations',
    'Press Coverage',
    'Judging Work of Others',
    'Original Contributions',
    'Scholarly Articles',
    'Leading or Critical Role',
    'High Salary',
  ],
  'EB-2 NIW': [
    'Advanced Degree',
    'Exceptional Ability - Official Recognition',
    'Exceptional Ability - Professional Membership',
    'Exceptional Ability - High Salary',
    'Exceptional Ability - Recognition for Achievements',
    'Exceptional Ability - Other Evidence',
    'National Interest Waiver - Substantial Merit',
    'National Interest Waiver - National Scope',
    'National Interest Waiver - Benefit to US',
  ],
};

// Default criteria for backward compatibility
const EB1A_CRITERIA = CRITERIA_BY_VISA_TYPE['EB-1A'];

// Toggle Button Component
const InputToggle: React.FC<{
  mode: 'text' | 'file';
  onModeChange: (mode: 'text' | 'file') => void;
}> = ({ mode, onModeChange }) => {
  return (
    <div className="inline-flex rounded-md border border-neutral-300 overflow-hidden">
      <button
        type="button"
        onClick={() => onModeChange('text')}
        className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${
          mode === 'text'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-neutral-700 hover:bg-neutral-50'
        }`}
      >
        <TextIcon className="h-3.5 w-3.5" />
        Text
      </button>
      <button
        type="button"
        onClick={() => onModeChange('file')}
        className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 border-l border-neutral-300 ${
          mode === 'file'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-neutral-700 hover:bg-neutral-50'
        }`}
      >
        <FileIcon className="h-3.5 w-3.5" />
        File
      </button>
    </div>
  );
};

const LetterCreateForm: React.FC<LetterCreateFormProps> = ({
  onCancel,
  onCreate,
  isSubmitting,
  caseId,
  visaType,
  error,
  onClearError,
}) => {
  const [refereeName, setRefereeName] = useState<string>('');
  const [recommenderBackground, setRecommenderBackground] =
    useState<string>('');
  const [relationshipToBeneficiary, setRelationshipToBeneficiary] =
    useState<string>('');
  const [achievementsToHighlight, setAchievementsToHighlight] =
    useState<string>('');
  const [whitelist, setWhitelist] = useState<string>('');
  const [blacklist, setBlacklist] = useState<string>('');
  const [refereeError, setRefereeError] = useState<string>('');
  const [backgroundError, setBackgroundError] = useState<string>('');
  const [targetWordCount, setTargetWordCount] = useState<number>(700);
  const [recommenderCountry, setRecommenderCountry] = useState<string>('');
  const [recommenderIsUSBased, setRecommenderIsUSBased] =
    useState<boolean>(true);

  // Toggle states for text vs file input
  const [backgroundMode, setBackgroundMode] = useState<'text' | 'file'>('text');
  const [relationshipMode, setRelationshipMode] = useState<'text' | 'file'>(
    'text'
  );
  const [achievementsMode, setAchievementsMode] = useState<'text' | 'file'>(
    'text'
  );

  // File states
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [relationshipFile, setRelationshipFile] = useState<File | null>(null);
  const [achievementsFile, setAchievementsFile] = useState<File | null>(null);
  const [templateFile, setTemplateFile] = useState<File | null>(null);

  // Drag state for visual feedback
  const [backgroundDragging, setBackgroundDragging] = useState(false);
  const [relationshipDragging, setRelationshipDragging] = useState(false);
  const [achievementsDragging, setAchievementsDragging] = useState(false);
  const [templateDragging, setTemplateDragging] = useState(false);

  // Criteria selection state
  const [selectedCriteria, setSelectedCriteria] = useState<string[]>([]);

  // Knowledge Base integration
  const [templateSource, setTemplateSource] = useState<'file' | 'kb'>('file');
  const [kbSearchQuery, setKbSearchQuery] = useState<string>('');
  const [kbSearchResults, setKbSearchResults] = useState<
    KnowledgeBaseDocument[]
  >([]);
  const [selectedKbDocument, setSelectedKbDocument] =
    useState<KnowledgeBaseDocument | null>(null);
  const [isSearchingKb, setIsSearchingKb] = useState(false);

  // Get criteria for the current visa type
  const availableCriteria =
    visaType && CRITERIA_BY_VISA_TYPE[visaType]
      ? CRITERIA_BY_VISA_TYPE[visaType]
      : EB1A_CRITERIA;

  // Search Knowledge Base for templates
  const searchKnowledgeBase = async () => {
    if (!kbSearchQuery.trim()) return;

    setIsSearchingKb(true);
    try {
      const results = await knowledgeBaseDocumentApi.search({
        query: kbSearchQuery,
        contentType: 'template',
        visaTypes: visaType ? [visaType] : undefined,
        limit: 10,
      });
      setKbSearchResults(results.documents || []);
    } catch (error) {
      console.error('Failed to search Knowledge Base:', error);
    } finally {
      setIsSearchingKb(false);
    }
  };

  // Auto-search when visa type changes
  useEffect(() => {
    if (visaType && templateSource === 'kb') {
      setKbSearchQuery(`${visaType} recommendation letter template`);
      searchKnowledgeBase();
    }
  }, [visaType, templateSource]);

  // Helper function to handle drag events
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (
    e: React.DragEvent,
    setDragging: (val: boolean) => void
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (
    e: React.DragEvent,
    setDragging: (val: boolean) => void
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (
    e: React.DragEvent,
    setFile: (file: File | null) => void,
    setDragging: (val: boolean) => void
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Validate file type
      const validTypes = ['.pdf', '.doc', '.docx', '.txt'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (validTypes.includes(fileExtension)) {
        setFile(file);
      } else {
        alert('Please upload a PDF, DOC, DOCX, or TXT file');
      }
    }
  };

  // Check if background is provided (text or file)
  const hasBackground =
    backgroundMode === 'text'
      ? recommenderBackground.trim().length > 0
      : backgroundFile !== null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanedRef = refereeName.trim();

    let hasErrors = false;

    if (!cleanedRef) {
      setRefereeError('Referee name is required');
      hasErrors = true;
    }

    // Validate recommender background is provided
    if (!hasBackground) {
      setBackgroundError('Recommender background is required (text or file)');
      hasErrors = true;
    }

    if (hasErrors) return;

    // Collect files from toggle fields
    const allFiles: FileWithType[] = [];

    // Add background file if in file mode
    if (backgroundMode === 'file' && backgroundFile) {
      allFiles.push({
        file: backgroundFile,
        documentType: 'BACKGROUND_INFO' as any,
        id: `bg-${Date.now()}`,
      });
    }

    // Add relationship file if in file mode
    if (relationshipMode === 'file' && relationshipFile) {
      allFiles.push({
        file: relationshipFile,
        documentType: 'SUPPORTING_DOCUMENT' as any,
        id: `rel-${Date.now()}`,
      });
    }

    // Add achievements file if in file mode
    if (achievementsMode === 'file' && achievementsFile) {
      allFiles.push({
        file: achievementsFile,
        documentType: 'SUPPORTING_DOCUMENT' as any,
        id: `ach-${Date.now()}`,
      });
    }

    // Add template file if provided
    if (templateFile) {
      allFiles.push({
        file: templateFile,
        documentType: 'TEMPLATE_EXAMPLE' as any,
        id: `template-${Date.now()}`,
      });
    }

    const payload: LetterCreatePayload = { letter_type: 'recommendation' };

    // Add Knowledge Base template reference if selected
    if (selectedKbDocument) {
      payload.metadata = {
        ...payload.metadata,
        template_kb_document_id: selectedKbDocument.id,
        template_kb_document_title: selectedKbDocument.title,
      };
    }
    if (cleanedRef) payload.referee_name = cleanedRef;

    // Only add text values if in text mode (not file mode)
    if (backgroundMode === 'text' && recommenderBackground.trim()) {
      payload.recommender_background = recommenderBackground.trim();
    }
    if (relationshipMode === 'text' && relationshipToBeneficiary.trim()) {
      payload.relationship_to_beneficiary = relationshipToBeneficiary.trim();
    }
    if (achievementsMode === 'text' && achievementsToHighlight.trim()) {
      payload.achievements_to_highlight = achievementsToHighlight.trim();
    }

    if (whitelist.trim()) payload.whitelist = whitelist.trim();
    if (blacklist.trim()) payload.blacklist = blacklist.trim();
    if (allFiles.length > 0) payload.files = allFiles;
    if (targetWordCount > 0) payload.target_word_count = targetWordCount;
    // Set country based on US-based selection
    payload.recommender_country = recommenderIsUSBased
      ? 'USA'
      : recommenderCountry.trim();
    payload.recommender_is_us_based = recommenderIsUSBased;
    if (selectedCriteria.length > 0)
      payload.selected_criteria = selectedCriteria;
    await onCreate(payload);
  };

  const toggleCriteria = (criterion: string) => {
    setSelectedCriteria(prev =>
      prev.includes(criterion)
        ? prev.filter(c => c !== criterion)
        : [...prev, criterion]
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="inline-flex items-center gap-1"
        >
          <BackIcon className="h-4 w-4" /> Back
        </Button>
        <h3 className="text-lg font-semibold text-neutral-900">
          Create letter
        </h3>
      </div>

      {/* Error display */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
          {onClearError && (
            <button
              type="button"
              onClick={onClearError}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      <Card padding="lg">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Recommender Information */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-neutral-700">
              Recommender Information
            </h4>

            <Input
              label="Recommender name"
              placeholder="Enter recommender full name"
              value={refereeName}
              onChange={e => {
                setRefereeName(e.target.value);
                if (refereeError) setRefereeError('');
              }}
              error={refereeError}
              helperText="Required. For example: Dr. John Smith"
              fullWidth
            />

            <div className="space-y-3">
              <label className="block text-sm font-medium text-neutral-700">
                Recommender location
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={recommenderIsUSBased}
                  onChange={e => {
                    setRecommenderIsUSBased(e.target.checked);
                    if (e.target.checked) {
                      setRecommenderCountry(''); // Clear country when switching to US-based
                    }
                  }}
                  className="h-5 w-5 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-neutral-900">
                    US-based recommender
                  </span>
                  <span className="text-xs text-neutral-500">
                    The recommender is located in the United States
                  </span>
                </div>
              </label>

              {!recommenderIsUSBased && (
                <Input
                  label="Recommender country"
                  placeholder="e.g., United Kingdom, Canada, Germany"
                  value={recommenderCountry}
                  onChange={e => setRecommenderCountry(e.target.value)}
                  helperText="Specify the country where the recommender is based"
                  fullWidth
                />
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-neutral-700">
                  Recommender background <span className="text-red-500">*</span>
                </label>
                <InputToggle
                  mode={backgroundMode}
                  onModeChange={mode => {
                    setBackgroundMode(mode);
                    if (backgroundError) setBackgroundError('');
                  }}
                />
              </div>
              {backgroundMode === 'text' ? (
                <Textarea
                  placeholder="Describe the recommender's qualifications, credentials, and expertise..."
                  helperText="Required. Provide the recommender's background to establish their credibility and authority."
                  value={recommenderBackground}
                  onChange={e => {
                    setRecommenderBackground(e.target.value);
                    if (backgroundError) setBackgroundError('');
                  }}
                  rows={4}
                  fullWidth
                  error={backgroundError}
                />
              ) : (
                <div>
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                      backgroundDragging
                        ? 'border-blue-500 bg-blue-50'
                        : backgroundError
                          ? 'border-red-300 bg-red-50'
                          : 'border-neutral-300 bg-white'
                    }`}
                    onDragOver={handleDragOver}
                    onDragEnter={e => handleDragEnter(e, setBackgroundDragging)}
                    onDragLeave={e => handleDragLeave(e, setBackgroundDragging)}
                    onDrop={e => {
                      handleDrop(e, setBackgroundFile, setBackgroundDragging);
                      if (backgroundError) setBackgroundError('');
                    }}
                  >
                    <input
                      type="file"
                      id="background-file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={e => {
                        setBackgroundFile(e.target.files?.[0] || null);
                        if (backgroundError) setBackgroundError('');
                      }}
                    />
                    <label htmlFor="background-file" className="cursor-pointer">
                      {backgroundFile ? (
                        <div className="flex flex-col items-center gap-2">
                          <FileIcon className="h-8 w-8 text-blue-600" />
                          <span className="text-sm font-medium text-neutral-900">
                            {backgroundFile.name}
                          </span>
                          <button
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              setBackgroundFile(null);
                            }}
                            className="text-xs text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <UploadIcon
                            className={`h-8 w-8 ${backgroundError ? 'text-red-400' : 'text-neutral-400'}`}
                          />
                          <span
                            className={`text-sm ${backgroundError ? 'text-red-600' : 'text-neutral-600'}`}
                          >
                            {backgroundDragging
                              ? 'Drop file here'
                              : 'Click to upload or drag and drop recommender background document'}
                          </span>
                          <span className="text-xs text-neutral-500">
                            PDF, DOC, DOCX, or TXT
                          </span>
                        </div>
                      )}
                    </label>
                  </div>
                  {backgroundError && (
                    <p className="mt-1 text-xs text-red-600">
                      {backgroundError}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-neutral-700">
                  Relationship to beneficiary
                </label>
                <InputToggle
                  mode={relationshipMode}
                  onModeChange={setRelationshipMode}
                />
              </div>
              {relationshipMode === 'text' ? (
                <Textarea
                  placeholder="Describe the nature and duration of the relationship..."
                  helperText="Explain how the recommender knows the beneficiary and in what capacity they worked together."
                  value={relationshipToBeneficiary}
                  onChange={e => setRelationshipToBeneficiary(e.target.value)}
                  rows={3}
                  fullWidth
                />
              ) : (
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    relationshipDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-neutral-300 bg-white'
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={e => handleDragEnter(e, setRelationshipDragging)}
                  onDragLeave={e => handleDragLeave(e, setRelationshipDragging)}
                  onDrop={e =>
                    handleDrop(e, setRelationshipFile, setRelationshipDragging)
                  }
                >
                  <input
                    type="file"
                    id="relationship-file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={e =>
                      setRelationshipFile(e.target.files?.[0] || null)
                    }
                  />
                  <label htmlFor="relationship-file" className="cursor-pointer">
                    {relationshipFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <FileIcon className="h-8 w-8 text-blue-600" />
                        <span className="text-sm font-medium text-neutral-900">
                          {relationshipFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            setRelationshipFile(null);
                          }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <UploadIcon className="h-8 w-8 text-neutral-400" />
                        <span className="text-sm text-neutral-600">
                          {relationshipDragging
                            ? 'Drop file here'
                            : 'Click to upload or drag and drop relationship document'}
                        </span>
                        <span className="text-xs text-neutral-500">
                          PDF, DOC, DOCX, or TXT
                        </span>
                      </div>
                    )}
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Beneficiary Achievements */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-neutral-700">
              Achievements to Highlight
            </h4>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-neutral-700">
                  Specific achievements
                </label>
                <InputToggle
                  mode={achievementsMode}
                  onModeChange={setAchievementsMode}
                />
              </div>
              {achievementsMode === 'text' ? (
                <Textarea
                  placeholder="List key achievements, awards, publications, or contributions to emphasize..."
                  helperText="Specify achievements that should be prioritized in the letter generation."
                  value={achievementsToHighlight}
                  onChange={e => setAchievementsToHighlight(e.target.value)}
                  rows={4}
                  fullWidth
                />
              ) : (
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    achievementsDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-neutral-300 bg-white'
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={e => handleDragEnter(e, setAchievementsDragging)}
                  onDragLeave={e => handleDragLeave(e, setAchievementsDragging)}
                  onDrop={e =>
                    handleDrop(e, setAchievementsFile, setAchievementsDragging)
                  }
                >
                  <input
                    type="file"
                    id="achievements-file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={e =>
                      setAchievementsFile(e.target.files?.[0] || null)
                    }
                  />
                  <label htmlFor="achievements-file" className="cursor-pointer">
                    {achievementsFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <FileIcon className="h-8 w-8 text-blue-600" />
                        <span className="text-sm font-medium text-neutral-900">
                          {achievementsFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            setAchievementsFile(null);
                          }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <UploadIcon className="h-8 w-8 text-neutral-400" />
                        <span className="text-sm text-neutral-600">
                          {achievementsDragging
                            ? 'Drop file here'
                            : 'Click to upload or drag and drop achievements document'}
                        </span>
                        <span className="text-xs text-neutral-500">
                          PDF, DOC, DOCX, or TXT
                        </span>
                      </div>
                    )}
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Visa Criteria Selection */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-neutral-700">
              {visaType
                ? `${visaType} Criteria to Emphasize`
                : 'Criteria to Emphasize'}
            </h4>
            <p className="text-xs text-neutral-600">
              Select which criteria this letter should address (optional)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {availableCriteria.map(criterion => (
                <label
                  key={criterion}
                  className="flex items-center gap-2 p-2 rounded border border-neutral-200 hover:bg-neutral-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCriteria.includes(criterion)}
                    onChange={() => toggleCriteria(criterion)}
                    className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-neutral-700">{criterion}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Advanced Options */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-neutral-700">
              Advanced Options
            </h4>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-neutral-700">
                  Template/Example Letter (Optional)
                </label>
                <div className="inline-flex rounded-md border border-neutral-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setTemplateSource('file')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${
                      templateSource === 'file'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-neutral-700 hover:bg-neutral-50'
                    }`}
                  >
                    <UploadIcon className="h-3.5 w-3.5" />
                    Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => setTemplateSource('kb')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 border-l border-neutral-300 ${
                      templateSource === 'kb'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-neutral-700 hover:bg-neutral-50'
                    }`}
                  >
                    <DatabaseIcon className="h-3.5 w-3.5" />
                    Knowledge Base
                  </button>
                </div>
              </div>
              <p className="text-xs text-neutral-600 mb-2">
                {templateSource === 'file'
                  ? 'Upload a template or example letter to guide the AI generation style and structure.'
                  : 'Search and select a template from the Knowledge Base.'}
              </p>

              {templateSource === 'file' ? (
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    templateDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-neutral-300 bg-white'
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={e => handleDragEnter(e, setTemplateDragging)}
                  onDragLeave={e => handleDragLeave(e, setTemplateDragging)}
                  onDrop={e =>
                    handleDrop(e, setTemplateFile, setTemplateDragging)
                  }
                >
                  <input
                    type="file"
                    id="template-file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={e => setTemplateFile(e.target.files?.[0] || null)}
                  />
                  <label htmlFor="template-file" className="cursor-pointer">
                    {templateFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <FileIcon className="h-8 w-8 text-blue-600" />
                        <span className="text-sm font-medium text-neutral-900">
                          {templateFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            setTemplateFile(null);
                          }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <UploadIcon className="h-8 w-8 text-neutral-400" />
                        <span className="text-sm text-neutral-600">
                          {templateDragging
                            ? 'Drop template file here'
                            : 'Click to upload or drag and drop template/example letter'}
                        </span>
                        <span className="text-xs text-neutral-500">
                          PDF, DOC, DOCX, or TXT
                        </span>
                      </div>
                    )}
                  </label>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search for templates..."
                      value={kbSearchQuery}
                      onChange={e => setKbSearchQuery(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          searchKnowledgeBase();
                        }
                      }}
                      fullWidth
                    />
                    <Button
                      type="button"
                      onClick={searchKnowledgeBase}
                      loading={isSearchingKb}
                      disabled={!kbSearchQuery.trim()}
                      className="inline-flex items-center gap-2"
                    >
                      <SearchIcon className="h-4 w-4" />
                      Search
                    </Button>
                  </div>

                  {selectedKbDocument && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <FileIcon className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-neutral-900">
                              {selectedKbDocument.title}
                            </span>
                          </div>
                          {typeof selectedKbDocument.metadata?.description ===
                            'string' && (
                            <p className="text-xs text-neutral-600">
                              {selectedKbDocument.metadata.description}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedKbDocument(null)}
                          className="text-xs text-red-600 hover:text-red-700 ml-2"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}

                  {kbSearchResults.length > 0 && !selectedKbDocument && (
                    <div className="border border-neutral-200 rounded-md max-h-60 overflow-y-auto">
                      {kbSearchResults.map(doc => (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={() => setSelectedKbDocument(doc)}
                          className="w-full text-left p-3 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <FileIcon className="h-4 w-4 text-neutral-400 mt-0.5" />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-neutral-900">
                                {doc.title}
                              </div>
                              {typeof doc.metadata?.description ===
                                'string' && (
                                <p className="text-xs text-neutral-600 mt-1">
                                  {doc.metadata.description}
                                </p>
                              )}
                              {doc.visaTypes && doc.visaTypes.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {doc.visaTypes.map(vt => (
                                    <span
                                      key={vt}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700"
                                    >
                                      {vt}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {kbSearchResults.length === 0 &&
                    kbSearchQuery &&
                    !isSearchingKb && (
                      <p className="text-sm text-neutral-500 text-center py-4">
                        No templates found. Try a different search query.
                      </p>
                    )}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Target letter length
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="300"
                  max="1500"
                  step="50"
                  value={targetWordCount}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    setTargetWordCount(isNaN(val) ? 700 : val);
                  }}
                  className="flex-1 h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex items-center gap-2 min-w-[120px]">
                  <input
                    type="number"
                    min="300"
                    max="1500"
                    step="50"
                    value={targetWordCount}
                    onChange={e => {
                      const val = parseInt(e.target.value, 10);
                      setTargetWordCount(isNaN(val) ? 700 : val);
                    }}
                    className="w-20 px-2 py-1 border border-neutral-300 rounded text-sm"
                  />
                  <span className="text-sm text-neutral-600">words</span>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Recommended: 600-800 words (2-3 pages). Higher values create
                more detailed letters.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Textarea
                label="Allowlist"
                placeholder="Comma-separated words/phrases to include"
                helperText="Strongly suggests the model to use these terms where relevant."
                value={whitelist}
                onChange={e => setWhitelist(e.target.value)}
                rows={2}
                fullWidth
              />
              <Textarea
                label="Blocklist"
                placeholder="Comma-separated words/phrases to avoid"
                helperText="Prevents using these terms unless required."
                value={blacklist}
                onChange={e => setBlacklist(e.target.value)}
                rows={2}
                fullWidth
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="space-y-3 pt-4 border-t border-neutral-200">
            {/* Show requirements helper when button would be disabled */}
            {(!refereeName.trim() || !hasBackground) && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <span className="text-amber-600 text-sm">Required to generate:</span>
                <ul className="text-sm text-amber-700 list-disc list-inside">
                  {!refereeName.trim() && <li>Recommender name</li>}
                  {!hasBackground && (
                    <li>
                      Recommender background{' '}
                      {backgroundMode === 'text' ? '(enter text)' : '(upload file)'}
                    </li>
                  )}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-2 justify-end">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || !refereeName.trim() || !hasBackground}
                className="inline-flex items-center gap-2"
              >
                <MagicIcon className="h-4 w-4" />
                <span>Generate letter</span>
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default LetterCreateForm;
